const STK500 = require('stk500');
// Web Serial API版のAvrgirl互換クラス（STK500準拠）
// STK500ライブラリを使用したWeb Serial API版Avrgirl
// 注意: このコードを使用する前に `npm install stk500` または `yarn add stk500` が必要

class ArduinoWriter {
    constructor(options) {
        this.board = options.board;
        this.port = options.port || null; // 事前選択されたポートを受け取り
        this.writer = null;
        this.reader = null;
        this.debug = options.debug || false;
        
        // ボード設定（avrgirl-arduino準拠）
        this.boardConfigs = {
            'pro-mini': {
                protocol: 'stk500v1',
                baud: 57600,
                signature: Buffer.from([0x1e, 0x95, 0x0f]),
                pageSize: 128,
                numPages: 256,
                timeout: 400,
                pagesizehigh: 0,
                pagesizelow: 128
            },
            'uno': {
                protocol: 'stk500v1', 
                baud: 115200,
                pageSize: 128,
                signature: Buffer.from([0x1e, 0x95, 0x0f]),
                timeout: 400,
                pagesizehigh: 0,
                pagesizelow: 128
            },
            'nano': {
                protocol: 'stk500v1',
                baud: 57600,
                pageSize: 128,
                signature: Buffer.from([0x1e, 0x95, 0x0f]),
                timeout: 400,
                pagesizehigh: 0,
                pagesizelow: 128
            }
        };
    }

    // avrgirlと同じインターフェース
    async flash(data, callback) {
        try {
            if (!('serial' in navigator)) {
                const error = new Error('Web Serial APIがサポートされていません');
                error.name = 'SecurityError';
                callback(error);
                return;
            }

            // データ形式変換（すべてBufferに統一）
            let firmware;
            if (data instanceof ArrayBuffer) {
                const textData = new TextDecoder().decode(data);
                if (textData.startsWith(':')) {
                    firmware = this.parseIntelHex(textData); // 既にBufferを返す
                } else {
                    firmware = Buffer.from(data); // Bufferに変換
                }
            } else if (typeof data === 'string' && data.startsWith(':')) {
                firmware = this.parseIntelHex(data); // 既にBufferを返す
            } else {
                firmware = Buffer.from(data); // Bufferに変換
            }
            
            if (this.debug) {
                console.log(`Firmware size: ${firmware.length} bytes`);
                console.log(`Firmware type: ${firmware.constructor.name}`); // Buffer確認用
            }
            
            // avrgirl-arduino風のアップロード
            await this.uploadWithSTK500(firmware);
            
            callback(null);
            
        } catch (error) {
            console.error('Upload error:', error);
            
            if (error.message.includes('No port selected') || error.message.includes('ポート選択')) {
                // DOMException.nameはgetter-onlyのため直接代入はstrict modeでTypeErrorになる
                Object.defineProperty(error, 'name', {value: 'SecurityError'});
            } else if (error.message.includes('sync') || error.message.includes('同期')) {
                error.code = 8;
            }
            
            callback(error);
        }
    }

    async uploadWithSTK500(firmware) {
        try {
            // 1. ポート選択・接続
            await this.connect();
            await this.openConnection();
            if (this.debug) {
                console.log(`Connected to port: ${this.port.getInfo().usbVendorId}:${this.port.getInfo().usbProductId}`);
            }
            
            // 2. リセット
            await this.resetBoard();
            if (this.debug) {
                console.log('reset complete.');
            }
            
            // 3. ファームウェアはflash()で既にBufferに変換済み
            if (this.debug) {
                console.log(`Using firmware Buffer: ${firmware.length} bytes, type: ${firmware.constructor.name}`);
            }

            // 4. Web Serial StreamをNode.js Stream風にラップ
            const webSerialStream = this.createWebSerialStream();
            
            // 5. STK500ライブラリを使用してbootload
            await this.executeSTK500Bootload(webSerialStream, firmware);
            
            console.log('flash complete.'); // avrgirl-arduino準拠
            
            // 6. 接続終了
            await this.disconnect();
            
        } catch (error) {
            try {
                await this.disconnect();
            } catch (e) {}
            throw error;
        }
    }

    // STK500ライブラリを使用したbootload実行
    executeSTK500Bootload(stream, firmware) {
        return new Promise((resolve, reject) => {
            const config = this.boardConfigs[this.board];
            
            // STK500インスタンスを作成
            const quiet = !this.debug;
            const stk = new STK500({
                quiet: quiet
            });
            
            // STK500のbootloadメソッドを実行
            stk.bootload(stream, firmware, config, (error) => {
                if (error) {
                    reject(error);
                } else {
                    resolve();
                }
            });
        });
    }

    // Web Serial APIをNode.js EventEmitter風のStreamにラップ
    createWebSerialStream() {
        const self = this;
        
        // EventEmitterライクなオブジェクトを作成
        const stream = {
            _listeners: new Map(),
            _reading: false,
            
            // Node.js Stream互換のwriteメソッド
            write: function(data, encoding, callback) {
                // Node.jsのstream.write(data, encoding, callback)形式に対応
                if (typeof encoding === 'function') {
                    callback = encoding;
                    encoding = null;
                }
                
                Promise.resolve().then(async () => {
                    try {
                        // BufferまたはUint8Arrayをそのまま書き込み
                        const uint8Data = data instanceof Uint8Array ? data : new Uint8Array(data);
                        await self.writer.write(uint8Data);
                        // デバッグ用に送信データを16進数で表示
                        if (self.debug) {
                            console.log('Sent:', Array.from(uint8Data).map(b => b.toString(16).padStart(2, '0')).join(''));
                        }                        
                        
                        if (callback) callback();
                    } catch (error) {
                        console.error('Write error:', error);
                        if (callback) callback(error);
                    }
                });
            },

            // EventEmitter風のイベントリスナー管理
            on: function(event, listener) {
                if (!this._listeners.has(event)) {
                    this._listeners.set(event, []);
                }
                this._listeners.get(event).push(listener);
                
                // 'data'イベントが登録されたら読み取り開始
                if (event === 'data' && !this._reading) {
                    this._reading = true;
                    this._startReading();
                }
            },
            
            removeListener: function(event, listener) {
                if (this._listeners.has(event)) {
                    const listeners = this._listeners.get(event);
                    const index = listeners.indexOf(listener);
                    if (index !== -1) {
                        listeners.splice(index, 1);
                    }
                    
                    // 'data'リスナーがなくなったら読み取り停止
                    if (event === 'data' && listeners.length === 0) {
                        this._reading = false;
                    }
                }
            },
            
            emit: function(event, ...args) {
                if (this._listeners.has(event)) {
                    for (const listener of this._listeners.get(event)) {
                        listener(...args);
                    }
                }
            },
            
            // Web Serial APIからの継続的な読み取り
            _startReading: async function() {
                while (this._reading) {
                    try {
                        const { value, done } = await self.reader.read();
                        if (done) {
                            this._reading = false;
                            break;
                        }
                        
                        if (value && value.length > 0) {
                            // 受信データをBufferに変換してdataイベントを発火
                            const buffer = Buffer.from(value);
                            // デバッグ用に受信データを16進数で表示
                            if (self.debug) {
                                console.log('Received:', Array.from(buffer).map(b => b.toString(16).padStart(2, '0')).join(''));
                            }
                            this.emit('data', buffer);
                        }
                    } catch (error) {
                        console.error('Read error:', error);
                        this.emit('error', error);
                        this._reading = false;
                        break;
                    }
                }
            }
        };

        return stream;
    }

    // 接続・リセット処理（avrgirl-arduino準拠）
    async connect() {
        if (!this.port) {
            this.port = await navigator.serial.requestPort();
        }
    }
    async openConnection() {
        const config = this.boardConfigs[this.board];
        
        await this.port.open({
            baudRate: config.baud,
            dataBits: 8,
            stopBits: 1,
            parity: 'none'
        });
        
        this.writer = this.port.writable.getWriter();
        this.reader = this.port.readable.getReader();
        
        // 初期化待機
        await this.delay(100);
    }

    async resetBoard() {
        // avrgirl-arduino準拠のリセット: DTR false(250ms) → true(50ms)
        await this.setDTR(false, 250);
        await this.setDTR(true, 50);
    }

    async setDTR(bool, timeout) {
        try {
            await this.port.setSignals({
                dataTerminalReady: bool,
                requestToSend: bool
            });
            await this.delay(timeout);
        } catch (error) {
            console.warn(`DTR control warning: ${error.message}`);
        }
    }

    async disconnect() {
        try {
            if (this.reader) {
                await this.reader.cancel();
                await this.reader.releaseLock();
            }
            if (this.writer) {
                await this.writer.releaseLock();
            }
            if (this.port) {
                await this.port.close();
            }
        } catch (error) {
            console.warn('Disconnect error:', error);
        }
    }

    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // Intel HEX解析（結果をBufferで返す）
    parseIntelHex(hexData) {
        const lines = hexData.split(/\r?\n/);
        const memory = new Map();
        let extendedAddress = 0;
        
        for (const line of lines) {
            if (!line.startsWith(':')) continue;
            
            const length = parseInt(line.substr(1, 2), 16);
            const address = parseInt(line.substr(3, 4), 16);
            const type = parseInt(line.substr(7, 2), 16);
            const data = line.substr(9, length * 2);
            
            switch (type) {
                case 0x00: // Data record
                    const fullAddress = extendedAddress + address;
                    for (let i = 0; i < length; i++) {
                        const byte = parseInt(data.substr(i * 2, 2), 16);
                        memory.set(fullAddress + i, byte);
                    }
                    break;
                    
                case 0x02: // Extended Segment Address
                    extendedAddress = parseInt(data, 16) << 4;
                    break;
                    
                case 0x04: // Extended Linear Address
                    extendedAddress = parseInt(data, 16) << 16;
                    break;
                    
                case 0x01: // End of File
                    break;
            }
        }
        
        if (memory.size === 0) {
            throw new Error('Intel HEXファイルにデータが含まれていません');
        }
        
        const maxAddress = Math.max(...memory.keys());
        
        // BufferでバイナリデータToを作成（重要！）
        const binary = Buffer.alloc(maxAddress + 1, 0xFF);
        
        for (const [address, byte] of memory) {
            binary[address] = byte;
        }
        if (this.debug) {
            console.log(`Parsed Intel HEX: ${memory.size} bytes, max address: 0x${maxAddress.toString(16)}`);
        }
        return binary; // Bufferを返す
    }
}
// avrgirlの代替エクスポート
module.exports = ArduinoWriter;
